import { cx } from '@emotion/css';
import { ArrowForwardRounded, PlaceRounded, PublicTwoTone } from '@mui/icons-material';
import { Divider, useMediaQuery } from '@sortlist-frontend/design-system/legacy';
import { SocialMediaType, useTrack } from '@sortlist-frontend/tracking';
import { Trans, useTranslation } from '@sortlist-frontend/translation/ssr';
import { useRadarTracker } from '@sortlist-frontend/user-tools';
import { getStorage, isNonEmptyPlainObject } from '@sortlist-frontend/utils';
import { Fragment, useEffect, useState } from 'react';

import { DirectBriefingButton } from '_components/Briefing/versions/DirectBriefing/button';
import { Obfuscate } from '_components/common/Obfuscate/Obfuscate';
import { usePublicAppContext } from '_core/context/public-app-context';
import { getPageTypeFromPathname } from '_core/utils/url/get-page-type';
import { GoogleMapCtn } from '_features/AgencyProfile/components/ContactPanel/GoogleMap';
import { SectionBox } from '_features/AgencyProfile/components/SectionBox';
import { agencyProfileConfig } from '_features/AgencyProfile/config';
import { useGetAgencyProfile } from '_features/AgencyProfile/hooks/useGetAgencyProfile';

import { isValidSocialMediaKey, socialMediaIconsList } from './social-media-icons.data';
import * as S from './styles';
import { type OfficeNode } from './utils';

interface Props {
  children?: never;
  className?: string;
  disableGoogleMaps?: boolean;
}

export const ContactPanel = (props: Props) => {
  const { className } = props;
  const { media_legacy } = useMediaQuery();
  const { t } = useTranslation(agencyProfileConfig.i18nNamespaces);
  const [selectedOffice, setSelectedOffice] = useState<OfficeNode | null>(null);
  const { locale, pageData } = usePublicAppContext();
  const { agencySlug, agencyUuid } = pageData as { agencySlug: string; agencyUuid: string };
  const { data: { agency } = {} } = useGetAgencyProfile({ agencySlug, locale: locale as string });
  const [sessionEntryUrl, setSessionEntryUrl] = useState('');
  const { track } = useTrack({ app: 'public', locale });
  const { track: radarTrack } = useRadarTracker();

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const url = getStorage('sessionEntryUrl') || window.location.href;
      setSessionEntryUrl(url.replace(/"/g, ''));
    }
  }, []);

  const offices = agency?.offices?.nodes?.filter((n): n is OfficeNode => n != null) ?? [];
  const { name, website: agencyWebsite, socialProfiles: agencySocialData, paidMember } = agency ?? { name: '' };

  const hasFreePlan = paidMember !== true;

  const showSocialMedia = isNonEmptyPlainObject(agencySocialData) && !hasFreePlan;

  const trackOpenWebsite = () => {
    track({
      slug: 'websiteOpened',
      data: {
        agencyUuid,
        agencySlug,
        page: 'agency-profile',
        pageType: 'agency-profile',
        element: 'contact-section',
        htmlElementTrigger: 'contact-section',
      },
    });
    radarTrack({
      eventName: 'websiteVisited',
      properties: {
        agency_slug: agencySlug,
      },
    });
  };

  return (
    <SectionBox
      id="contact"
      className={cx(className, 'sm:py-24 max-sm:py-16')}
      title={t('agency:profile.contact.title')}
      subtitle={
        <Trans i18nKey={'agency:profile.contact.subtitle'}>
          <b>Contact details</b> of {{ name }}
        </Trans>
      }>
      <div className="lg:px-16 px-8 relative">
        <div className="border border-secondary-900 rounded-lg shadow-2 overflow-hidden layout-column layout-gt-sm-row">
          {media_legacy.gtSm && offices.length > 0 && (
            <GoogleMapCtn
              className="width-100 width-gt-sm-65"
              agencyName={name}
              agencySlug={name}
              offices={offices}
              selectedOffice={selectedOffice}
            />
          )}
          <div className="layout-column flex-gt-sm-35">
            <p className="bold h6 sm:pt-12 pt-4 pb-4 px-4">{t('agency:profile.contact.detailsTitle')}</p>
            <div className="p-4 layout-column">
              <DirectBriefingButton
                className="flex-1"
                cta="contact-panel"
                agencySlug={agencySlug as string}
                size={'md'}
                buttonStyle="primary"
                buttonVariant="raised"
                label={t('agency:profile.contact.contactBtnLabel', { name })}
              />
            </div>
            {agencyWebsite != null && !hasFreePlan && (
              <Fragment>
                <Divider />
                <div className="layout-row layout-align-start-center px-4">
                  <PublicTwoTone style={{ fontSize: 16 }} />
                  <Obfuscate
                    obfuscate={hasFreePlan}
                    role="link"
                    href={`${agencyWebsite}?utm_source=sortlist&utm_medium=profile&utm_campaign=${encodeURIComponent(sessionEntryUrl)}`}
                    target="_blank"
                    rel={hasFreePlan ? 'nofollow' : 'noopener'}
                    className="btn small text-truncate btn-default btn-secondary btn-md flex-1 text-left px-4"
                    onClick={trackOpenWebsite}>
                    {agencyWebsite}
                  </Obfuscate>
                  <ArrowForwardRounded style={{ fontSize: 16 }} />
                </div>
              </Fragment>
            )}
            {offices.length > 0 && (
              <Fragment>
                <Divider />
                <S.ScrollingLocations className="bg-secondary-50 layout-row overflow-y-scroll">
                  <S.LocationIcon className="sticky layout-column layout-align-start-start py-6 pl-4 pr-1">
                    <PlaceRounded style={{ fontSize: 16 }} />
                  </S.LocationIcon>
                  <ul className="layout-column list-reset flex-1">
                    {offices.map((office, index) => (
                      <li key={office.id} className="layout-column">
                        <S.Location className="cursor-pointer pl-0 pr-3 py-1" onClick={() => setSelectedOffice(office)}>
                          <div
                            className={cx(
                              { 'bg-secondary-300': office.id === selectedOffice?.id && media_legacy.gtSm },
                              'small layout-column py-2 px-3 rounded-sm',
                            )}>
                            {office.position === 1 && (
                              <span className="bold">{t('agency:profile.contact.headquarter')}</span>
                            )}
                            <span className="text-break-word">{office.address}</span>
                          </div>
                        </S.Location>
                        {index + 1 !== offices.length ? <Divider className="ml-0" /> : null}
                      </li>
                    ))}
                  </ul>
                </S.ScrollingLocations>
                <Divider />
                {showSocialMedia && (
                  <div className="layout-row p-4">
                    {Object.keys(agencySocialData).map(
                      (key) =>
                        key !== 'website' &&
                        isValidSocialMediaKey(key) &&
                        (agencySocialData as Record<string, string | null | undefined>)[key] != null && (
                          <Obfuscate
                            role={'link'}
                            obfuscate={true}
                            href={(agencySocialData as Record<string, string | null | undefined>)[key] as string}
                            onClick={() => {
                              track({
                                slug: 'socialMediaOpened',
                                data: {
                                  agencyName: name,
                                  agencySlug,
                                  agencyUuid,
                                  type: key,
                                  socialLinkType: key as SocialMediaType,
                                  pageType: getPageTypeFromPathname(window.location.pathname),
                                },
                              });
                            }}
                            target="_blank"
                            rel="nofollow"
                            key={`${name}-${key}`}
                            className="mr-2 text-secondary-700 bg-secondary-300 rounded-full layout-column layout-align-center-center p-2">
                            <S.SocialIcon src={socialMediaIconsList[key]} alt={`${name}-${key}`} />
                          </Obfuscate>
                        ),
                    )}
                  </div>
                )}
              </Fragment>
            )}
          </div>
        </div>
      </div>
    </SectionBox>
  );
};
